import {
  FETCH_ALL_USERS,
  FETCH_ALL_USERS_SUCCESS,
  FETCH_ALL_USERS_FAILED,
  EDIT_USER,
  EDIT_USER_SUCCESS,
  EDIT_USER_FAILED,
  DELETE_USER,
  DELETE_USER_SUCCESS,
  DELETE_USER_FAILED,
  FETCH_ALL_USERS_STATIC,
  FETCH_ALL_USERS_STATIC_SUCCESS,
  FETCH_ALL_USERS_STATIC_FAILED,
  USER_DELETED,
  FETCH_ALL_DRIVERS,
  FETCH_ALL_DRIVERS_SUCCESS,
  FETCH_ALL_DRIVERS_FAILED
} from "../store/types";
import { firebase } from '../config/configureFirebase';
import { onValue, push, update, off, get, remove, child, ref } from "firebase/database";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { signOut } from "firebase/auth";
import { startMetric, endMetric, logInfo, logWarn, logError } from '../utils/performanceMonitor';
import store from '../store/store';
import { GetDistance } from '../other/GeoFunctions';
import { findNearbyDrivers, buildActiveDriversList, createPublicCache, isValidLocation } from '../other/nearbyDrivers';
import { mergeDriverLocationUpdates } from '../other/mapDriverRefresh';
import { isFreshLocation } from '../other/locationThrottle';

// MEJORA: Caché dinámico para fetchDrivers con TTL según tipo de conexión
// El TTL se ajusta automáticamente vía ProductionConfig.getCacheTTL()
const driverCache = {
  data: null,
  timestamp: null,
  center: null,
  ttl: 30000, // TTL por defecto (será sobrescrito dinámicamente)
  getEffectiveTTL() {
    // Intentar obtener TTL dinámico desde ProductionConfig
    try {
      // En mobile-app, ProductionConfig está disponible
      if (typeof global !== 'undefined' && global.ProductionConfig) {
        const ttl = global.ProductionConfig.getCacheTTL();
        const interval = typeof global.ProductionConfig.getUpdateInterval === 'function'
          ? global.ProductionConfig.getUpdateInterval()
          : 0;
        // El TTL debe vencer antes del siguiente tick del intervalo del mapa o el refresco se salta un ciclo
        return interval > 10000 ? Math.min(ttl, interval - 5000) : ttl;
      }
    } catch (error) {
      // Silenciar error y usar fallback
    }
    return this.ttl; // Fallback al TTL por defecto
  }
};

const CACHE_CENTER_TOLERANCE_KM = 1;
const RECENT_DRIVERS_MAX_MS = 5 * 60 * 1000;
const driverPublicCache = createPublicCache({
  read: async (uid) => {
    const snap = await get(firebase.driverPublicRef(uid));
    return snap.exists() ? snap.val() : null;
  },
});

let serverTimeOffset = 0;
let serverTimeOffsetWatched = false;
/** Hora del servidor: `ts` de /locations la pone el servidor y el reloj del teléfono puede estar mal. */
const serverNow = () => {
  if (!serverTimeOffsetWatched && firebase.database) {
    serverTimeOffsetWatched = true;
    onValue(ref(firebase.database, '.info/serverTimeOffset'), (snap) => {
      serverTimeOffset = Number(snap.val()) || 0;
    });
  }
  return Date.now() + serverTimeOffset;
};

/** Perfil público sin caché de los conductores a los que se enviará la solicitud (estado `queue`/`active` actual). */
export const refreshDriversAvailability = (uids) => driverPublicCache.refresh([...new Set((uids || []).filter(Boolean))]);

let inflightDriversFetch = null;
let inflightLocationTick = null;
let lastLocationTickAt = 0;
const LOCATION_TICK_MIN_GAP_MS = 8000;

const hasRecentDrivers = () => !!(
  driverCache.data &&
  driverCache.timestamp &&
  Date.now() - driverCache.timestamp < RECENT_DRIVERS_MAX_MS
);

// Función helper para crear timeout promise
const createTimeoutPromise = (timeoutMs) => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Request timeout')), timeoutMs);
  });
};

// MEJORA: Función helper para verificar si el caché es válido (con TTL dinámico)
const isCacheValid = (center) => {
  if (!driverCache.data || !driverCache.timestamp) {
    return false;
  }
  if (center) {
    const cached = driverCache.center;
    if (!cached) return false;
    if (GetDistance(center.lat, center.lng, cached.lat, cached.lng) > CACHE_CENTER_TOLERANCE_KM) {
      return false;
    }
  }
  const now = Date.now();
  const effectiveTTL = driverCache.getEffectiveTTL();
  return (now - driverCache.timestamp) < effectiveTTL;
};

// MEJORA 5: Sistema de retry con backoff exponencial
// Mejora la experiencia en conexiones inestables con retry automático
const retryWithBackoff = async (
  fn,
  options = {}
) => {
  const {
    maxRetries = 3,
    initialDelay = 1000, // 1 segundo
    maxDelay = 10000, // 10 segundos máximo
    backoffFactor = 2,
    onRetry = null
  } = options;

  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // No retry si es el último intento o si es un error que no requiere retry
      if (attempt === maxRetries) {
        break;
      }
      
      // No retry para ciertos errores que no tienen sentido reintentar
      if (error.message && (
        error.message.includes('permission-denied') ||
        error.message.includes('invalid-argument') ||
        error.message.includes('unauthenticated')
      )) {
        throw error;
      }
      
      // Calcular delay con backoff exponencial
      const delay = Math.min(
        initialDelay * Math.pow(backoffFactor, attempt),
        maxDelay
      );
      
      // Callback opcional para logging (pasar metadata incluyendo retry count)
      if (onRetry) {
        onRetry(attempt + 1, maxRetries, delay, error, { retryCount: attempt + 1 });
      }
      
      // Esperar antes del siguiente intento
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

export const fetchUsers = () => (dispatch) => {

  const {
    usersRef,
    allLocationsRef
  } = firebase;

  dispatch({
    type: FETCH_ALL_DRIVERS,
    payload: null
  });
  onValue(usersRef, async snapshot => {
    if (snapshot.val()) {
      const locationdata = await get(allLocationsRef);
      const locations = locationdata.val();
      const data = snapshot.val();
      const arr = Object.keys(data)
      .filter(i => data[i].usertype!='admin')
      .map(i => {
        data[i].id = i;
        data[i].location = locations && locations[i] ? locations[i] : null;
        return data[i];
      });
      dispatch({
        type: FETCH_ALL_USERS_SUCCESS,
        payload: arr
      });
    } else {
      dispatch({
        type: FETCH_ALL_USERS_FAILED,
        payload: "No users available."
      });
    }
  });
};


export const fetchUsersOnce = (opts = {}) => (dispatch, getState) => {

  const {
    usersRef,
    allLocationsRef
  } = firebase;

  const includeLocations = opts.includeLocations !== false;
  const force = !!opts.force;
  const existing = getState()?.usersdata?.staticusers;
  // Reutilizar datos en memoria (evita re-leer users+locations al abrir Métricas/Red)
  if (!force && existing && existing.length > 0) {
    return;
  }

  dispatch({
    type: FETCH_ALL_USERS_STATIC,
    payload: null
  });
  onValue(usersRef, async snapshot => {
    if (snapshot.val()) {
      let locations = null;
      if (includeLocations) {
        try {
          const locationdata = await get(allLocationsRef);
          locations = locationdata.val();
        } catch (e) {
          console.warn('fetchUsersOnce: locations skip', e?.message || e);
        }
      }
      const data = snapshot.val();
      const arr = Object.keys(data)
      .map(i => {
        data[i].id = i;
        data[i].location = locations && locations[i] ? locations[i] : null;
        return data[i];
      });
      dispatch({
        type: FETCH_ALL_USERS_STATIC_SUCCESS,
        payload: arr
      })
    } else {
      dispatch({
        type: FETCH_ALL_USERS_STATIC_FAILED,
        payload: "No users available."
      });
    }
  },{onlyOnce: true});
  
};
export const clearFetchDrivers = () => (dispatch) => {
  const {
    allLocationsRef,
  } = firebase;
  off(allLocationsRef);
}

export const fetchDrivers = (appType, forceRefresh = false) => async(dispatch) => {

  const {
    allLocationsRef,
    locationsByGeohashRef,
    activeDriversPublicRef,
    settingsRef,
  } = firebase;

  // OPTIMIZACIÓN: Limpiar listeners anteriores para evitar acumulación
  off(allLocationsRef);

  // MEJORA 6: Iniciar métrica de rendimiento
  const metric = startMetric('fetchDrivers');

  const resolveCenter = () => {
    try {
      const state = store.getState();
      const pickup = state?.tripdata?.pickup;
      if (pickup?.lat != null && pickup?.lng != null) {
        return { lat: Number(pickup.lat), lng: Number(pickup.lng), source: 'pickup' };
      }
      const gps = state?.gpsdata?.location;
      if (gps?.lat != null && gps?.lng != null) {
        return { lat: Number(gps.lat), lng: Number(gps.lng), source: 'gps' };
      }
    } catch (_) { /* ignore */ }
    return null;
  };

  const center = resolveCenter();

  // MEJORA URGENTE 2: Verificar caché antes de hacer la petición
  if (!forceRefresh && isCacheValid(center)) {
    const cacheAge = Date.now() - driverCache.timestamp;
    logInfo('fetchDrivers: Cache hit', { cacheAge });
    endMetric(metric, true, { cacheHit: true });
    dispatch({
      type: FETCH_ALL_DRIVERS_SUCCESS,
      payload: driverCache.data
    });
    return driverCache.data;
  }

  // Una sola búsqueda a la vez: varias pantallas llaman fetchDrivers casi al mismo tiempo
  if (inflightDriversFetch) {
    endMetric(metric, true, { joinedInflight: true });
    return inflightDriversFetch;
  }

  dispatch({
    type: FETCH_ALL_USERS,
    payload: null
  });

  let retryCount = 0; // Contador de retries para métricas
  const onRetryLog = (label) => (attempt, maxRetries, delay, error, metadata) => {
    retryCount = metadata?.retryCount || attempt;
    logWarn(`fetchDrivers: Retry ${attempt}/${maxRetries} para ${label} después de ${delay}ms`, {
      error: error?.message,
      retryCount
    });
  };

  const snapshotVal = async (refOrQuery) => {
    const snap = await get(refOrQuery);
    return snap.exists() ? snap.val() : null;
  };

  let publicReads = 0;
  const getDriverPublics = async (uids, locations) => {
    const { publics, reads } = await driverPublicCache.get(uids, locations);
    publicReads += reads;
    return publics;
  };

  const fetchPromise = new Promise(async (resolve, reject) => {
    try {
      const settingsdata = await retryWithBackoff(
        () => get(settingsRef),
        { maxRetries: 2, initialDelay: 500, onRetry: onRetryLog('settings') }
      );
      const settings = settingsdata.val() || {};

      let maxDrivers = 40;
      try {
        if (typeof global !== 'undefined' && global.ProductionConfig) {
          maxDrivers = global.ProductionConfig.getMaxDrivers();
        }
      } catch (error) {
        // default
      }

      let drivers = [];
      let lookup = {};
      if (appType === 'web') {
        const [publics, locations] = await Promise.all([
          retryWithBackoff(() => snapshotVal(activeDriversPublicRef), { maxRetries: 2, initialDelay: 1000, onRetry: onRetryLog('drivers_public') }),
          retryWithBackoff(() => snapshotVal(allLocationsRef), { maxRetries: 2, initialDelay: 1000, onRetry: onRetryLog('locations') }),
        ]);
        drivers = buildActiveDriversList({ publics, locations, settings, center, now: serverNow() });
        lookup = { mode: 'web' };
      } else if (center) {
        const result = await findNearbyDrivers({
          center,
          settings,
          maxDrivers,
          queryPrefix: (prefix) => snapshotVal(locationsByGeohashRef(prefix)),
          getAllLocations: () => retryWithBackoff(
            () => snapshotVal(allLocationsRef),
            { maxRetries: 3, initialDelay: 1000, maxDelay: 5000, onRetry: onRetryLog('locations') }
          ),
          getDriverPublics,
          now: serverNow(),
        });
        drivers = result.drivers;
        lookup = { mode: 'nearby', ...result.stats, publicReads };
      } else {
        // Sin GPS/pickup aún: no vaciar el mapa ni marcar FAILED (evita parpadeo “sin conductores”).
        lookup = { mode: 'noCenter' };
        endMetric(metric, true, { driversCount: 0, centerSource: null, retries: retryCount, ...lookup });
        if (hasRecentDrivers()) {
          dispatch({
            type: FETCH_ALL_DRIVERS_SUCCESS,
            payload: driverCache.data
          });
          resolve(driverCache.data);
        } else {
          resolve([]);
        }
        return;
      }

      if (drivers.length === 0) {
        endMetric(metric, true, { driversCount: 0, centerSource: center?.source || null, retries: retryCount, ...lookup });
        // Lista vacía real: SUCCESS [] (no FAILED→null) para no borrar marcadores previos
        // si el reducer interpreta null como “limpiar”. Conservar caché reciente si existe.
        if (hasRecentDrivers()) {
          dispatch({
            type: FETCH_ALL_DRIVERS_SUCCESS,
            payload: driverCache.data
          });
          resolve(driverCache.data);
        } else {
          driverCache.data = [];
          driverCache.timestamp = Date.now();
          driverCache.center = center ? { lat: center.lat, lng: center.lng } : null;
          dispatch({
            type: FETCH_ALL_DRIVERS_SUCCESS,
            payload: []
          });
          resolve([]);
        }
        return;
      }

      driverCache.data = drivers;
      driverCache.timestamp = Date.now();
      driverCache.center = center ? { lat: center.lat, lng: center.lng } : null;

      endMetric(metric, true, {
        driversCount: drivers.length,
        centerSource: center?.source || null,
        retries: retryCount,
        ...lookup
      });

      dispatch({
        type: FETCH_ALL_DRIVERS_SUCCESS,
        payload: drivers
      });

      resolve(drivers);
    } catch (error) {
      logError('fetchDrivers: Error en fetchPromise', error);
      reject(error);
    }
  });

  const inflight = fetchPromise
    .catch(() => null)
    .finally(() => {
      if (inflightDriversFetch === inflight) inflightDriversFetch = null;
    });
  inflightDriversFetch = inflight;

  // MEJORA: Timeout dinámico según tipo de conexión
  // WiFi=15s, 4G=30s, 3G=60s
  let timeout = 10000; // Por defecto
  try {
    if (typeof global !== 'undefined' && global.ProductionConfig) {
      timeout = global.ProductionConfig.getFetchDriversTimeout();
    }
  } catch (error) {
    // Usar valor por defecto en caso de error
  }
  const timeoutPromise = createTimeoutPromise(timeout);

  try {
    const result = await Promise.race([fetchPromise, timeoutPromise]);
    return result;
  } catch (error) {
    const timedOut = error.message === 'Request timeout';
    // Conservar la última lista buena: vaciarla hace parpadear los marcadores hasta el siguiente ciclo
    if (hasRecentDrivers()) {
      logWarn('fetchDrivers: fallo o timeout, usando última lista', { timeout: timedOut, cacheAge: Date.now() - driverCache.timestamp });
      endMetric(metric, true, { timeout: timedOut, staleCache: true });
      dispatch({
        type: FETCH_ALL_DRIVERS_SUCCESS,
        payload: driverCache.data
      });
      return driverCache.data;
    }
    logError('fetchDrivers: Error final', error, { timeout: timedOut });
    endMetric(metric, false, {
      timeout: timedOut,
      error: error.message
    });
    dispatch({
      type: FETCH_ALL_DRIVERS_FAILED,
      payload: timedOut ? "Request timeout. Please try again." : "Error fetching drivers: " + error.message
    });
    return [];
  }
};

/**
 * Tick ligero: solo /locations/{uid} para conductores ya listados (mapa inicio pasajero).
 * No mueve driverCache.timestamp para no saltarse fetchDrivers completo.
 */
export const refreshDriverLocations = (maxUids = 40) => async (dispatch, getState) => {
  if (inflightDriversFetch) {
    return getState()?.usersdata?.drivers || null;
  }
  const now = Date.now();
  if (now - lastLocationTickAt < LOCATION_TICK_MIN_GAP_MS) {
    return getState()?.usersdata?.drivers || null;
  }
  if (inflightLocationTick) {
    return inflightLocationTick;
  }

  const drivers = getState()?.usersdata?.drivers;
  if (!Array.isArray(drivers) || drivers.length === 0) {
    return drivers;
  }

  const { userLocationRef } = firebase;
  const cap = Math.max(1, Number(maxUids) || 40);
  const uids = [...new Set(drivers.map((d) => d.uid || d.id).filter(Boolean))].slice(0, cap);
  const serverNowMs = serverNow();

  const work = (async () => {
    const updatesByUid = {};
    await Promise.all(uids.map(async (uid) => {
      try {
        const snap = await get(userLocationRef(uid));
        if (!snap.exists()) return;
        const loc = snap.val();
        if (!isValidLocation(loc) || !isFreshLocation(loc, serverNowMs)) return;
        updatesByUid[uid] = loc;
      } catch (_) { /* ignore */ }
    }));

    const { next, changed } = mergeDriverLocationUpdates(drivers, updatesByUid);
    if (!changed) return drivers;

    if (driverCache.data) {
      driverCache.data = mergeDriverLocationUpdates(driverCache.data, updatesByUid).next;
    }

    dispatch({
      type: FETCH_ALL_DRIVERS_SUCCESS,
      payload: next,
    });
    return next;
  })();

  inflightLocationTick = work.finally(() => {
    lastLocationTickAt = Date.now();
    if (inflightLocationTick === work) inflightLocationTick = null;
  });

  return inflightLocationTick;
};

export const addUser = (userdata) => (dispatch) => {
  const {
    usersRef
  } = firebase;

  dispatch({
    type: EDIT_USER,
    payload: userdata
  });

  delete userdata.tableData;

  push(usersRef, userdata).then(() => {
    dispatch({
      type: EDIT_USER_SUCCESS,
      payload: null
    });
  }).catch((error) => {
    dispatch({
      type: EDIT_USER_FAILED,
      payload: error
    });
  });
}

const SERVER_MANAGED_USER_FIELDS = [
  'walletBalance',
  'walletReferral',
  'networkWithdrawPaydayKey',
  'networkWithdrawPaydayAmount',
  'networkWithdrawMonthKey',
  'networkWithdrawMonthAmount',
  'networkEarningsMonthKey',
  'networkEarningsMonthAmount',
  'leaderWeekly',
];

export const editUser = (id, user) => (dispatch) => {

  const {
    singleUserRef
  } = firebase;

  // Clonar: no mutar el objeto del dispatch (el reducer necesita id en el payload)
  const payload = { ...user, id };
  dispatch({
    type: EDIT_USER,
    payload
  });
  const editedUser = { ...user };
  delete editedUser.id;
  delete editedUser.tableData;
  // Las listas del admin se cargan una vez: reescribir estos campos con datos viejos borraría
  // bonos y retiros registrados por el servidor después de cargar la lista.
  SERVER_MANAGED_USER_FIELDS.forEach((field) => {
    delete editedUser[field];
  });
  update(singleUserRef(id), editedUser);
}

export const updateUserCar = (id, data) => (dispatch) => {
  const {
    singleUserRef
  } = firebase;

  dispatch({
    type: EDIT_USER,
    payload: data  
  });
  update(singleUserRef(id),data);
}

export const updateLicenseImage = (uid, imageBlob, imageType) => async (dispatch) => {

  const {
    singleUserRef,
    driverDocsRef,
    driverDocsRefBack,
    verifyIdImageRef
  } = firebase;

  let profile = {};
  if(imageType === 'licenseImage'){
    await uploadBytesResumable(driverDocsRef(uid),imageBlob);
    let image = await getDownloadURL(driverDocsRef(uid));
    profile.licenseImage = image;
  }
  if(imageType === 'licenseImageBack'){
    await uploadBytesResumable(driverDocsRefBack(uid),imageBlob);
    let image1 = await getDownloadURL(driverDocsRefBack(uid));
    profile.licenseImageBack = image1;
  }
  if(imageType === 'verifyIdImage'){
    await uploadBytesResumable(verifyIdImageRef(uid),imageBlob);
    let image1 = await getDownloadURL(verifyIdImageRef(uid));
    profile.verifyIdImage = image1;
  }
  update(singleUserRef(uid),profile);
  dispatch({
    type: EDIT_USER,
    payload: uid
  });
};

export const deleteUser = (uid) => (dispatch) => {

  const {
    auth,
    walletHistoryRef,
    singleUserRef,
    userNotificationsRef,
    carsRef,
    carEditRef
  } = firebase;

  dispatch({
    type: DELETE_USER,
    payload: uid
  });

  if (auth.currentUser.uid === uid) {
    off(singleUserRef(uid));
    off(walletHistoryRef(uid));
    off(userNotificationsRef(uid));
  }

  onValue(singleUserRef(uid), userdata => {
    const profile = userdata.val();
    if (!profile) {
      dispatch({
        type: DELETE_USER_FAILED,
        payload: 'User not found'
      });
      return;
    }
    if(profile.usertype === 'driver'){
      onValue(carsRef(uid, profile.usertype), carssnapshot => {
        let cars = carssnapshot.val();
        if (cars) {
          const arr = Object.keys(cars);
          for(let i = 0; i < arr.length; i++){
            remove(carEditRef(arr[i]));
          }
        }
      }, { onlyOnce: true });
    }

    // Liberar /usedreferral (email/teléfono) para que pueda volver a usar código de referido.
    try {
      const { usedreferralRef } = firebase;
      get(usedreferralRef).then((snap) => {
        const data = snap.val();
        if (!data) return;
        const email = profile.email ? String(profile.email).trim().toLowerCase() : '';
        const phone = profile.mobile ? String(profile.mobile).trim() : '';
        Object.keys(data).forEach((key) => {
          const row = data[key] || {};
          const rowEmail = row.email ? String(row.email).trim().toLowerCase() : '';
          const rowPhone = row.phone || row.mobile
            ? String(row.phone || row.mobile).trim()
            : '';
          const rowUid = row.uid ? String(row.uid).trim() : '';
          if (
            (rowUid && rowUid === uid) ||
            (email && rowEmail && rowEmail === email) ||
            (phone && rowPhone && rowPhone === phone)
          ) {
            remove(child(usedreferralRef, key)).catch(() => { /* ignore */ });
          }
        });
      }).catch(() => { /* Cloud Function userDelete también limpia */ });
    } catch (_) { /* ignore */ }
    
    remove(singleUserRef(uid)).then(() => {
      if (auth.currentUser.uid === uid) {
        signOut(auth);
        dispatch({
          type: USER_DELETED,
          payload: null
        });
      } else {
        remove(singleUserRef(uid)).then(() => {
          dispatch({
            type: DELETE_USER_SUCCESS,
            payload: null
          });
        }).catch((error) => {
          dispatch({
            type: DELETE_USER_FAILED,
            payload: error
          });
        });
      }
    });
  },{onlyOnce:true});
}
